<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAiStatus } from 'dashboard/composables/useAiStatus';

const { t } = useI18n();
const { aiStatus, isLoading } = useAiStatus();

const showBadge = computed(() => !isLoading.value && aiStatus.value !== null);
const isAiActive = computed(() => aiStatus.value === true);

const badgeIcon = computed(() =>
  isAiActive.value
    ? t('CONVERSATION.AI_STATUS.AI_ICON')
    : t('CONVERSATION.AI_STATUS.HUMAN_ICON')
);

const badgeText = computed(() =>
  isAiActive.value
    ? t('CONVERSATION.AI_STATUS.AI_ACTIVE')
    : t('CONVERSATION.AI_STATUS.HUMAN_CONTROL')
);
</script>

<template>
  <span
    v-if="showBadge"
    class="ai-badge"
    :class="isAiActive ? 'ai-badge--ai' : 'ai-badge--human'"
  >
    <span class="ai-badge__icon">{{ badgeIcon }}</span>
    <span class="ai-badge__text">{{ badgeText }}</span>
    <span v-if="isAiActive" class="ai-badge__pulse" />
  </span>
</template>

<style lang="scss" scoped>
.ai-badge {
  @apply relative inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs
         font-bold border flex-shrink-0 whitespace-nowrap;

  &--ai {
    @apply bg-n-teal-9 border-n-teal-10 text-white shadow-md;
    animation: ai-glow 2s ease-in-out infinite;
  }

  &--human {
    @apply bg-n-amber-3 border-n-amber-6 text-n-amber-11 shadow-sm;
  }

  &__icon {
    @apply text-sm leading-none;
  }

  &__text {
    @apply tracking-wide;
  }

  &__pulse {
    @apply absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-n-teal-11;
    animation: pulse-dot 1.5s ease-in-out infinite;
  }
}

@keyframes ai-glow {
  0%,
  100% {
    box-shadow: 0 0 4px 0 rgba(0, 190, 170, 0.3);
  }
  50% {
    box-shadow: 0 0 12px 2px rgba(0, 190, 170, 0.5);
  }
}

@keyframes pulse-dot {
  0%,
  100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.5;
    transform: scale(1.3);
  }
}
</style>
